// This file contains global CSS constants for spacing, colors, and other styles.

import { lighten, darken } from '../utils/colorUtils';

// spacing constants
export const cssSpacingXs = '4px';
export const cssSpacingSm = '8px';
export const cssSpacingMd = '12px';
export const cssSpacingLg = '16px';
export const cssSpacingXl = '32px';
export const cssSpacingXxl = '40px';
export const cssSpacingXxxl = '60px';

// Base primary color (all other colors will be derived from this)
export const cssColorPrimary = '#163860';

// Derived colors
export const cssColorSecondary = lighten(cssColorPrimary, 0.25); // Lighter version of primary
export const cssColorLight = lighten(cssColorPrimary, 0.13); // Very light version for backgrounds
export const cssColorDark = darken(cssColorPrimary, 0.08); // Slightly darker version

// Standard semantic colors with slight influence from primary
export const cssColorSuccess = '#4caf50'; // Keep standard green for clarity
export const cssColorWarning = '#ff9800'; // Keep standard orange/amber for clarity
export const cssColorError = '#d32f2f'; // Keep standard red for clarity
export const cssColorInfo = '#2196f3'; // Keep standard blue for clarity

// font constants
export const cssFontSizeSmall = '12px';
export const cssFontSizeMedium = '15px';
export const cssFontSizeLarge = '16px';
export const cssFontSizeBody = '16px';

// button constants
export const buttonPrimaryBackgroundColor = cssColorPrimary;
export const buttonPrimaryTextColor = '#ffffff';
export const buttonSecondaryBackgroundColor = cssColorLight;

// input constants
export const inputMaxWidth = 300;
export const labelMaxWidth = 250;
export const inputMinWidth = '100px';
export const maxTableWidth = 1200;

// Create a structured colors object for theme use
export const colors = {
  background: {
    dark: cssColorDark,
    default: cssColorLight,
    paper: '#ffffff',
  },
  border: {
    dark: `${cssColorPrimary}20`,
    light: 'rgba(255, 255, 255, 0.5)', // Primary with transparency
    medium: `${cssColorPrimary}30`, // Primary with more opacity
  },
  dark: cssColorDark,
  error: cssColorError,
  info: cssColorInfo,
  light: cssColorLight,
  primary: cssColorPrimary,
  secondary: cssColorSecondary,
  success: cssColorSuccess,
  text: {
    light: '#ffffff',
    primary: '#333340',
    secondary: cssColorSecondary,
  },
  warning: cssColorWarning,
};

// Create a typography object for theme use
export const typography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: {
    large: cssFontSizeLarge,
    medium: cssFontSizeMedium,
    small: cssFontSizeSmall,
  },
  fontWeight: {
    bold: 700,
    medium: 500,
    regular: 400,
    semibold: 600,
  },
};

export const layout = {
  borderRadius: {
    medium: '8px',
    small: '4px',
    large: '20px',
  },
  shadows: {
    medium: '0 4px 8px rgba(0,0,0,0.15)',
    small: '0 2px 4px rgba(0,0,0,0.1)',
  },
};

// Enterprise-clean form tokens
export const formUi = {
  card: {
    background: '#ffffff',
    border: '1px solid rgba(22, 56, 96, 0.14)',
    borderHover: '1px solid rgba(22, 56, 96, 0.24)',
    radius: '10px',
    shadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
  },
  field: {
    borderDefault: 'rgba(22, 56, 96, 0.28)',
    borderFocus: '#163860',
    borderHover: 'rgba(22, 56, 96, 0.42)',
    helperSpacingTop: '4px',
    inputHeight: 44,
    /** Space between label row and control inside a field */
    verticalGap: '4px',
  },
  spacing: {
    cardPadding: '14px',
    /** Max content width for list (single-column) request forms */
    formColumnListMax: 760,
    /** Max content width for grid request forms */
    formColumnGridMax: 1200,
    /** Space between stacked form fields (list layout, workflow response stack) */
    sectionGap: '8px',
    rowGap: '8px',
  },
};

// Add missing dimensions
export const cssLogoHeight = '64px';
export const cssMaxContentWidth = '300px';
export const cssMaxStackWidth = '400px';
export const cssMaxCardWidth = '1100px';
export const cssMinCardHeight = '600px';
export const cssMinButtonWidth = '200px';

// Add missing typography values
export const cssLineHeightRelaxed = '1.6';
export const cssLetterSpacingWide = '0.5px';
